import { createFileRoute } from "@tanstack/react-router";

import { createHead } from "@/components/createHead";
import { UserPage } from "@/components/UserPage";
import {
  CustomNodeHistoryItem,
  getMonetizedFeeds,
  getPublicFeeds,
  searchCustomNodes,
  SearchResult,
} from "@/lib/api";

export const Route = createFileRoute("/_marketplace-layout/user/$userId/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      feeds: search.feeds || "[]",
    } as {
      feeds?: string;
    };
  },
  loader: async ({ params, location }) => {
    const id = (params as { userId: string }).userId;
    const [ads, publicFeeds, customNodes] = await Promise.all([
      getMonetizedFeeds({
        user_id: id,
        days: 30,
        page_size: 20,
      }),
      getPublicFeeds({
        days: 30,
        user_id: id,
        page_size: 20,
      }),
      searchCustomNodes({
        query: "",
        sort_by: "install_count",
        order: "desc",
        page_size: 20,
        user_id: id,
      }),
    ]);

    const user =
      publicFeeds.results[0]?.user ||
      ads.results[0]?.user ||
      customNodes.results[0]?.user;

    if (!user) {
      throw new Error("User not found");
    }

    user.id = parseInt(id);

    const visible = JSON.parse(
      (location.search as { feeds: string }).feeds || "[]",
    ) as string[];
    const adsResults = ads.results as Omit<SearchResult, "algorithm">[];

    return {
      ads: adsResults,
      customNodes: customNodes.results as Omit<
        CustomNodeHistoryItem,
        "component_metadata"
      >[],
      publicFeeds: publicFeeds.results as Omit<SearchResult, "algorithm">[],
      user,
      visible,
    };
  },
  head: ({ loaderData }) => {
    return createHead({
      title: `@${loaderData!.user.handle}'s Profile | Graze`,
      description: `Public profile for @${loaderData!.user.handle}.`,
      pathname: `/user/${loaderData!.user.id}`,
      ogImage: `/user/$userId/og.png`,
      ogParams: { userId: loaderData!.user.id },
    });
  },
});

function RouteComponent() {
  const { customNodes, ads, publicFeeds, user, visible } =
    Route.useLoaderData();

  return (
    <UserPage
      adFeeds={ads as SearchResult[]}
      publicFeeds={publicFeeds as SearchResult[]}
      visibleIds={visible}
      customNodes={customNodes as CustomNodeHistoryItem[]}
      user={user}
      adAction="view-ads-profile"
    />
  );
}
